import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, Bell } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface WatchEntry { symbol: string; news: boolean; price: boolean }

export const useWatchlist = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["watchlist"],
    queryFn: async () => (await api.get("/account/watchlist")).data,
    enabled: !!user,
  });
};

export const WatchButton = ({ symbol }: { symbol: string }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data } = useWatchlist();
  const entry: WatchEntry | undefined = data?.watchlist?.find((w: WatchEntry) => w.symbol === symbol);
  const [open, setOpen] = useState(false);
  const [news, setNews] = useState(true);
  const [price, setPrice] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (entry) { setNews(!!entry.news); setPrice(!!entry.price); }
  }, [entry?.symbol, entry?.news, entry?.price]);

  const save = async () => {
    if (!news && !price) { toast.error("Pick at least one alert type"); return; }
    setBusy(true);
    try {
      await api.post("/account/watchlist", { symbol, news, price });
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
      toast.success(`${symbol} added to your watchlist`);
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/account/watchlist/${symbol}`);
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
      toast.success(`${symbol} removed from your watchlist`);
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => {
      if (o && !user) { toast.error("Sign in to build your watchlist"); return; }
      setOpen(o);
    }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="watch-coin-button"
          className={`rounded-md font-bold ${entry
            ? "border-[#F59E0B]/60 bg-[#F59E0B]/10 text-[#F59E0B] hover:bg-[#F59E0B]/20 hover:text-[#F59E0B]"
            : "border-white/20 bg-transparent text-white hover:bg-white/5"}`}>
          <Star className={`h-4 w-4 mr-1.5 ${entry ? "fill-[#F59E0B]" : ""}`} />
          {entry ? "Watching" : "Watch"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 bg-[#121212] border-white/10 text-white">
        <p className="font-news font-bold text-sm mb-1">Watch {symbol}</p>
        <p className="text-xs text-gray-400 mb-4">Choose the alerts you want — one or both.</p>
        <label className="flex items-start gap-2.5 cursor-pointer mb-3" data-testid="watch-news-toggle">
          <Checkbox checked={news} onCheckedChange={(v: any) => setNews(!!v)} className="mt-0.5" />
          <span>
            <span className="text-sm font-semibold block">News alerts</span>
            <span className="text-xs text-gray-500">Push when a story about {symbol} breaks</span>
          </span>
        </label>
        <label className="flex items-start gap-2.5 cursor-pointer mb-4" data-testid="watch-price-toggle">
          <Checkbox checked={price} onCheckedChange={(v: any) => setPrice(!!v)} className="mt-0.5" />
          <span>
            <span className="text-sm font-semibold block">Price alerts</span>
            <span className="text-xs text-gray-500">Push when {symbol} moves ±5% in an hour</span>
          </span>
        </label>
        <p className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-4">
          <Bell className="h-3 w-3" /> Alerts arrive as notifications — enable them via the bell icon.
        </p>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={busy} data-testid="watch-save-button"
            className="flex-1 bg-[#7C3AED] hover:bg-[#6b2fd6] text-white font-bold">
            {entry ? "Update" : "Start watching"}
          </Button>
          {entry && (
            <Button size="sm" variant="outline" onClick={remove} disabled={busy} data-testid="watch-remove-button"
              className="border-white/20 bg-transparent text-gray-300 hover:bg-white/5 hover:text-white">
              Remove
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
